import { useState, useEffect } from "react";
import { useFlow } from "../../contexts/flow";
import TriggerList from "../TriggerList";
import Pagination from "../Pagination";
import { useSearchParams } from "react-router-dom";
import serviceMethods from "../../service";
import { useAuth } from "../../contexts/auth";
import BreadcrumbWrapper from "../BreadcrumbWrapper/index";

const TriggerManager = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [triggers, setTriggers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const { flowDetails, setFlowDetails, allFlows, setAllFlows } = useFlow();
  const { user } = useAuth();
  const itemsPerPage = 10;

  const { getAllFlows, getAllTriggers, getFlowTriggers, getFlowStageTriggers } = serviceMethods;

  const handleFlowSelector = (e) => {
    const selectedFlow = allFlows.find((flow) => flow.id === e.target.value);
    if (selectedFlow) {
      setFlowDetails(selectedFlow);
      setSearchParams({ flow: selectedFlow.id });
    } else {
      setFlowDetails({
        id: null,
        flowTitle: null,
      });
      setSearchParams({});
    }
  };

  const handleStageSelector = (e) => {
    const selectedStage = flowDetails?.flowStages.find((stage) => stage.id === e.target.value);
    if (selectedStage) {
      setSearchParams({ flow: flowDetails.id, stage: selectedStage.id });
    } else {
      setSearchParams({ flow: flowDetails.id });
    }
  };

  const fetchData = async (flowId, stageId) => {
    try {
      const flowsRes = await getAllFlows();
      const flows = Array.isArray(flowsRes) ? flowsRes : [];
      setAllFlows(flows);

      let data;
      if (stageId) {
        const stageTriggerRes = await getFlowStageTriggers(stageId);
        data = await stageTriggerRes.json();
      } else if (flowId) {
        const flowTriggerRes = await getFlowTriggers(flowId);
        data = await flowTriggerRes.json();
      } else {
        const allTriggerRes = await getAllTriggers();
        data = await allTriggerRes.json();
      }
      const triggerData = Array.isArray(data) ? data : [];
      setTriggers(triggerData);
    } catch {
      console.error("Unable to fetch data in trigger manager");
    }
  };

  useEffect(() => {
    const flowId = searchParams.get("flow");
    const stageId = searchParams.get("stage");
    fetchData(flowId, stageId);
  }, [searchParams]);

  // Pagination logic
  const indexOfLastTrigger = currentPage * itemsPerPage;
  const indexOfFirstTrigger = indexOfLastTrigger - itemsPerPage;
  const currentTriggers = triggers.slice(indexOfFirstTrigger, indexOfLastTrigger);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="flex flex-col gap-4">
      <BreadcrumbWrapper crumbArray={[{ path: "/trigger-manager", label: "Trigger Manager" }]} />
      <div className="mx-auto w-full rounded-lg">
        <div className="flex justify-between">
          <h1 className="mb-4 text-2xl font-bold">Trigger Manager</h1>

          <div className="flex gap-4 pr-4">
            <select
              className="rounded border"
              value={searchParams.get("flow") || ""}
              onChange={(e) => {
                handleFlowSelector(e);
              }}
            >
              <option value="">Select Flow</option>
              {(Array.isArray(allFlows) ? allFlows : [])?.map((flow) => (
                <option value={flow.id || ""} key={flow.id}>
                  {flow.flowTitle}
                </option>
              ))}
            </select>

            <select
              className={`rounded border ${!flowDetails.id ? "bg-gray-200" : ""}`}
              value={searchParams.get("stage") || ""}
              disabled={flowDetails.id ? false : true}
              onChange={(e) => {
                handleStageSelector(e);
              }}
            >
              <option value="">Select Stage</option>
              {flowDetails?.flowStages?.map((stage) => (
                <option value={stage.id || ""} key={stage.id}>
                  {stage.title}
                </option>
              ))}
            </select>
          </div>
        </div>
        <TriggerList triggers={currentTriggers} setTriggers={setTriggers} userId={user?.id} />
        <Pagination
          totalItems={triggers.length}
          itemsPerPage={itemsPerPage}
          paginate={paginate}
          currentPage={currentPage}
        />
      </div>
    </div>
  );
};

export default TriggerManager;
